import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";

import Modal, { type ModalAction } from "../../../../components/modal/Modal";
import Stepper from "../../../../components/stepper/Stepper";
import SvgLoadingAnimation from "../../../../components/loading";
import Button from "../../../../components/button/Button";
import Input from "../../../../components/form-item/Input";
import UserAvatar from "../../../../components/avatar/UserAvatar";
import DateDisplay, { DATE_DISPLAY_TYPE } from "../../../../components/date-display/DateDisplay";
import { getFileIconFromExtension } from "../../../../components/file-icon/fileIcon";
import {
  Shot,
  useMoveShotContentsMutation,
} from "../../../../app/services/shots";
import {
  MediaItem,
  mediaItemsApi,
  useGetMediaItemsQuery,
} from "../../../../app/services/mediaItems";
import {
  ShotComment,
  useLazyGetShotCommentsQuery,
} from "../../../../app/services/shotComments";
import {
  getShotTableStatusKey,
  SHOT_TABLE_STATUS_LABELS,
} from "../../../../constant/shotTableStatus";

/**
 * ショット間移動ウィザード（3 ステップ）。
 *
 * - Step1: 移動するメディア（バリエーション単位。履歴テイク・比較グループは一体で移動）
 * - Step2: 移動するコメント。移動対象メディアにピンされたコメントは自動選択（解除不可）、
 *   返信は親コメントと一体で移動（個別トグル不可）
 * - Step3: 移動先ショットの単一選択
 *
 * 実際の移動は POST /api/{project_id}/shots/{shot_id}/move。BE 側でも
 * 「メディアに紐づくコメント」「親子コメント」の自動同梱が行われるため、
 * FE の選択 UI は BE の挙動をそのまま可視化したものになる。
 */

const STEP_LABELS = ["メディア選択", "コメント選択", "移動先選択"];
const MIN_STEP = 1;
const MAX_STEP = STEP_LABELS.length;

/** コメント全件取得時のページサイズ（BE ShotCommentPagination.max_limit と同値） */
const COMMENTS_PAGE_LIMIT = 100;
/** コメント全件取得の安全上限ページ数 */
const COMMENTS_MAX_PAGES = 50;

/** Preview の ComparisonSlide / MediaItemTable と同じ「ベース名_a」検出ルール */
const COMPARISON_SUFFIX_RE = /^(.+)_([a-zA-Z])$/;

type CommentNode = { comment: ShotComment; replies: ShotComment[] };

type FetchShotComments = ReturnType<typeof useLazyGetShotCommentsQuery>[0];

/** ショットの全コメントをページングで取得し、会話順（作成日時昇順）で返す */
const fetchAllShotComments = async (
  fetchComments: FetchShotComments,
  shotId: string,
): Promise<ShotComment[]> => {
  const acc: ShotComment[] = [];
  let offset = 0;
  for (let page = 0; page < COMMENTS_MAX_PAGES; page++) {
    const res = await fetchComments(
      {
        shot_id: shotId,
        filter: "all",
        include_replies: true,
        limit: COMMENTS_PAGE_LIMIT,
        offset,
      },
      false,
    ).unwrap();
    const data = res.data;
    acc.push(...(data?.comments ?? []));
    if (!data?.has_next || data.next_offset == null) break;
    offset = data.next_offset;
  }
  acc.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  return acc;
};

// ステップコンテンツを中央寄せ + 読みやすい幅に絞るラッパー
const StepContent = styled.div`
  width: 100%;
  max-width: 740px;
  margin-inline: auto;
`;

const SelectableRow = styled.div<{ $selected: boolean; $disabled?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  border-radius: 4px;
  padding: 8px;
  background-color: ${({ $selected }) =>
    $selected ? "var(--soremo-background)" : "transparent"};

  @media (hover: hover) {
    &:hover {
      background-color: var(--soremo-background);
    }
  }
`;

const SelectIcon = styled.span.attrs({
  className: "material-symbols-rounded",
})<{ $selected: boolean; $locked?: boolean }>`
  font-size: 24px;
  flex-shrink: 0;
  color: ${({ $selected, $locked }) =>
    $selected && !$locked ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
`;

// サムネイル（画像があれば表示、なければファイル種別アイコン）
const ThumbBox = styled.div<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 4px;
  overflow: hidden;
  background: var(--soremo-background);
  border: 1px solid var(--soremo-border);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

// コメント本文の 2 行クランプ
const CommentExcerpt = styled.p.attrs({
  className: "bodytext-13",
})`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

const EmptyState = styled.div.attrs({
  className: "bodytext-13 u-text-light-gray",
})`
  text-align: center;
  padding: 24px 8px;
`;

// ===== Step1 の選択単位 =====

/**
 * メディア選択の最小単位。
 * - 単独バリエーション（履歴テイク込み）
 * - 比較グループ（「ベース名_a」「ベース名_b」…）はグループ一体
 */
interface MoveUnit {
  id: string;
  label: string;
  comparisonBase: string | null;
  /** 比較グループ内のバリエーション名（A, B, ...順） */
  variationNames: string[];
  /** この単位に含まれる全メディアアイテム id（履歴テイク含む） */
  itemIds: string[];
  /** グループ内の最大テイク数 */
  takeCount: number;
  thumbnail: string | null;
  fileName: string;
}

const buildMoveUnits = (items: MediaItem[]): MoveUnit[] => {
  // 表示順は variation_order 昇順で安定化（ShotEditModal の初期ロードと同じ）
  const sorted = [...items].sort((a, b) => a.variation_order - b.variation_order);

  // バリエーション名でグループ化し、各グループは updated_at 降順（先頭 = 最新テイク）
  const groupMap = new Map<string, MediaItem[]>();
  for (const item of sorted) {
    const existing = groupMap.get(item.variation_name);
    if (existing) {
      existing.push(item);
    } else {
      groupMap.set(item.variation_name, [item]);
    }
  }
  const groups = [...groupMap.entries()].map(([name, groupItems]) => ({
    name,
    items: [...groupItems].sort(
      (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    ),
  }));

  // 同じベース名のバリエーションが 2 件以上あるベースのみ比較グループ化
  const baseCount = new Map<string, number>();
  groups.forEach((g) => {
    const m = g.name.match(COMPARISON_SUFFIX_RE);
    if (m) baseCount.set(m[1], (baseCount.get(m[1]) || 0) + 1);
  });

  const units: MoveUnit[] = [];
  const unitByBase = new Map<string, MoveUnit>();
  groups.forEach((g) => {
    const m = g.name.match(COMPARISON_SUFFIX_RE);
    const base = m && (baseCount.get(m[1]) ?? 0) >= 2 ? m[1] : null;
    const latest = g.items[0];
    if (base) {
      let unit = unitByBase.get(base);
      if (!unit) {
        unit = {
          id: latest.id,
          label: base,
          comparisonBase: base,
          variationNames: [],
          itemIds: [],
          takeCount: 0,
          thumbnail: latest.thumbnail,
          fileName: latest.file_name,
        };
        unitByBase.set(base, unit);
        units.push(unit);
      }
      unit.variationNames.push(g.name);
      unit.itemIds.push(...g.items.map((i) => i.id));
      unit.takeCount = Math.max(unit.takeCount, g.items.length);
    } else {
      units.push({
        id: latest.id,
        label: g.name,
        comparisonBase: null,
        variationNames: [g.name],
        itemIds: g.items.map((i) => i.id),
        takeCount: g.items.length,
        thumbnail: latest.thumbnail,
        fileName: latest.file_name,
      });
    }
  });

  // 比較グループ内はサフィックス文字順（A, B, C ...）に揃える
  units.forEach((unit) => {
    if (unit.comparisonBase) {
      unit.variationNames.sort((a, b) =>
        (a.match(COMPARISON_SUFFIX_RE)?.[2] ?? "").localeCompare(
          b.match(COMPARISON_SUFFIX_RE)?.[2] ?? "",
        ),
      );
    }
  });

  return units;
};

const comparisonLetters = (unit: MoveUnit): string =>
  unit.variationNames
    .map((name) => name.match(COMPARISON_SUFFIX_RE)?.[2].toUpperCase() ?? "")
    .join(" / ");

// ===== Props =====

interface ShotMoveWizardModalProps {
  projectId: string;
  /** 移動元ショット */
  sourceShot: Shot;
  /** 移動先候補（移動元は内部で除外する） */
  shots: Shot[];
  onClose: () => void;
  /** 移動成功後に呼ばれる（一覧の再読み込み用） */
  onMoved: () => void;
}

const ShotMoveWizardModal: React.FC<ShotMoveWizardModalProps> = ({
  projectId,
  sourceShot,
  shots,
  onClose,
  onMoved,
}) => {
  const dispatch = useDispatch();
  const [step, setStep] = useState(MIN_STEP);

  // ── Step1: メディア ──
  const { data: mediaData, isLoading: isLoadingMedia } = useGetMediaItemsQuery({
    shot_id: sourceShot.id,
  });
  const [selectedUnitIds, setSelectedUnitIds] = useState<Set<string>>(new Set());

  const units = useMemo(
    () => buildMoveUnits(mediaData?.data?.media_items ?? []),
    [mediaData],
  );

  const mediaItemsById = useMemo(() => {
    const map = new Map<string, MediaItem>();
    (mediaData?.data?.media_items ?? []).forEach((item) => map.set(item.id, item));
    return map;
  }, [mediaData]);

  /** 選択中の全メディアアイテム id（履歴テイク・比較グループ含む） */
  const selectedMediaIds = useMemo(() => {
    const ids = new Set<string>();
    units.forEach((unit) => {
      if (selectedUnitIds.has(unit.id)) {
        unit.itemIds.forEach((id) => ids.add(id));
      }
    });
    return ids;
  }, [units, selectedUnitIds]);

  const toggleUnit = (unitId: string) => {
    setSelectedUnitIds((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }
      return next;
    });
  };

  const allUnitsSelected = units.length > 0 && selectedUnitIds.size === units.length;
  const toggleAllUnits = () => {
    setSelectedUnitIds(allUnitsSelected ? new Set() : new Set(units.map((u) => u.id)));
  };

  // ── Step2: コメント ──
  const [fetchComments] = useLazyGetShotCommentsQuery();
  const [allComments, setAllComments] = useState<ShotComment[] | null>(null);
  const [commentsError, setCommentsError] = useState(false);
  const [manualCommentIds, setManualCommentIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const comments = await fetchAllShotComments(fetchComments, sourceShot.id);
        if (!cancelled) setAllComments(comments);
      } catch {
        if (!cancelled) setCommentsError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceShot.id, fetchComments]);

  /** 親コメント id → 返信リスト（表示ツリーと自動同梱の閉包計算で共用） */
  const repliesByParent = useMemo(() => {
    const map = new Map<string, ShotComment[]>();
    (allComments ?? []).forEach((c) => {
      if (!c.parent_id) return;
      const existing = map.get(c.parent_id);
      if (existing) {
        existing.push(c);
      } else {
        map.set(c.parent_id, [c]);
      }
    });
    return map;
  }, [allComments]);

  /** 親コメント（トップレベル）と返信のツリー */
  const commentNodes = useMemo(
    () =>
      (allComments ?? [])
        .filter((c) => !c.parent_id)
        .map((c) => ({ comment: c, replies: repliesByParent.get(c.id) ?? [] })),
    [allComments, repliesByParent],
  );

  /** 移動対象メディアにピンされているか（= 自動選択・解除不可） */
  const isMediaLocked = (c: ShotComment): boolean =>
    !!c.media_item_id && selectedMediaIds.has(c.media_item_id);

  /**
   * 実際に移動されるコメント id の集合（BE の自動同梱と同じ閉包）。
   * - 移動対象メディアにピンされたコメント
   * - 手動選択された親コメント
   * - 上記の親／返信（親と返信は常に一体）
   */
  const effectiveCommentIds = useMemo(() => {
    const list = allComments ?? [];
    const byId = new Map(list.map((c) => [c.id, c]));

    const selected = new Set<string>();
    const pending: string[] = [];
    const add = (id: string) => {
      if (!selected.has(id)) {
        selected.add(id);
        pending.push(id);
      }
    };

    list.forEach((c) => {
      if (c.media_item_id && selectedMediaIds.has(c.media_item_id)) add(c.id);
      if (!c.parent_id && manualCommentIds.has(c.id)) add(c.id);
    });
    // 選択済み集合が安定するまで、親と返信を辿って拡張する
    while (pending.length > 0) {
      const id = pending.pop() as string;
      const comment = byId.get(id);
      if (comment?.parent_id) add(comment.parent_id);
      (repliesByParent.get(id) ?? []).forEach((reply) => add(reply.id));
    }
    return selected;
  }, [allComments, repliesByParent, selectedMediaIds, manualCommentIds]);

  /** 親コメント行の解除可否（メディア起因の自動選択は解除できない） */
  const isParentLocked = (node: CommentNode): boolean =>
    isMediaLocked(node.comment) || node.replies.some(isMediaLocked);

  const toggleComment = (node: CommentNode) => {
    if (isParentLocked(node)) return;
    setManualCommentIds((prev) => {
      const next = new Set(prev);
      if (next.has(node.comment.id)) {
        next.delete(node.comment.id);
      } else {
        next.add(node.comment.id);
      }
      return next;
    });
  };

  // ── Step3: 移動先 ──
  const [targetShotId, setTargetShotId] = useState<string | null>(null);
  const [targetSearch, setTargetSearch] = useState("");

  /** 移動先にできるショット（移動元を除く全ショット） */
  const otherShots = useMemo(
    () => shots.filter((s) => s.id !== sourceShot.id),
    [shots, sourceShot.id],
  );

  /** 検索キーワードで絞り込んだ移動先候補 */
  const targetCandidates = useMemo(() => {
    const keyword = targetSearch.trim().toLowerCase();
    if (!keyword) return otherShots;
    return otherShots.filter((s) => s.shot_name.toLowerCase().includes(keyword));
  }, [otherShots, targetSearch]);

  const targetShot = useMemo(
    () => shots.find((s) => s.id === targetShotId) ?? null,
    [shots, targetShotId],
  );

  // ── 移動実行 ──
  const [moveShotContents, { isLoading: isMoving }] = useMoveShotContentsMutation();

  const handleMove = async () => {
    if (!targetShotId || isMoving) return;
    try {
      await moveShotContents({
        project_id: projectId,
        shot_id: sourceShot.id,
        target_shot_id: targetShotId,
        media_item_ids: [...selectedMediaIds],
        comment_ids: [...effectiveCommentIds],
      }).unwrap();
      // 両ショットのメディア一覧キャッシュを無効化（Preview 等の再取得用）
      dispatch(mediaItemsApi.util.invalidateTags(["MediaItems"]));
      toast.success(`「${targetShot?.shot_name ?? ""}」へ移動しました`);
      onMoved();
      onClose();
    } catch {
      toast.error("移動に失敗しました");
    }
  };

  // ── ステップ制御 ──
  const hasSelection = selectedMediaIds.size > 0 || effectiveCommentIds.size > 0;

  const isStepValid = (stepNumber: number): boolean => {
    switch (stepNumber) {
      case 1:
        return true; // メディア未選択でも「コメントのみ移動」が可能
      case 2:
        return hasSelection;
      case 3:
        return hasSelection && !!targetShotId;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (!isStepValid(step)) return;
    if (step < MAX_STEP) {
      setStep((prev) => prev + 1);
      return;
    }
    handleMove();
  };

  const isLastStep = step === MAX_STEP;

  const primaryAction: ModalAction = {
    label: isLastStep ? "移動する" : "次へ",
    onClick: handleNext,
    disabled: !isStepValid(step) || isMoving,
    loading: isLastStep && isMoving,
    rightIcon: isLastStep ? undefined : "chevron_right",
  };

  const secondaryAction: ModalAction = {
    label: step === MIN_STEP ? "キャンセル" : "戻る",
    onClick: step === MIN_STEP ? onClose : () => setStep((prev) => prev - 1),
    disabled: isMoving,
    leftIcon: step === MIN_STEP ? undefined : "chevron_left",
  };

  /** 選択サマリー（Step2 以降の見出し下に表示） */
  const selectionSummary = `メディアアイテム ${selectedMediaIds.size} 件 / コメント ${effectiveCommentIds.size} 件`;

  // ── レンダリング ──

  const renderMediaStep = () => (
    <div className="u-col u-gap8">
      <div>
        <h2 className="heading-spacing-18">移動するメディアを選択</h2>
        <p className="bodytext-13 u-text-light-gray u-mt8">
          「{sourceShot.shot_name}」から移動するメディアを選択してください。
          過去テイク（履歴）とオプション（候補）グループは一体で移動されます。
          メディアを選択せず、コメントだけを移動することもできます。
        </p>
      </div>

      {isLoadingMedia ? (
        <div className="u-col-center u-w100 u-mt24">
          <SvgLoadingAnimation />
        </div>
      ) : units.length === 0 ? (
        <EmptyState>このショットにはメディアがありません</EmptyState>
      ) : (
        <>
          <div className="u-row u-justify-end u-w100">
            <Button
              size="small"
              theme="tertiary"
              variant="text"
              width="fit"
              onClick={toggleAllUnits}
            >
              {allUnitsSelected ? "すべて解除" : "すべて選択"}
            </Button>
          </div>
          <div className="u-col u-w100">
            {units.map((unit) => {
              const selected = selectedUnitIds.has(unit.id);
              return (
                <SelectableRow
                  key={unit.id}
                  $selected={selected}
                  onClick={() => toggleUnit(unit.id)}
                >
                  <div className="u-row u-gap8 u-items-center">
                    <SelectIcon $selected={selected}>
                      {selected ? "check_circle" : "radio_button_unchecked"}
                    </SelectIcon>
                    <ThumbBox $size={32}>
                      {unit.thumbnail ? (
                        <img src={unit.thumbnail} alt="" loading="lazy" />
                      ) : (
                        <span
                          className="material-symbols-rounded u-text-light-gray"
                          style={{ fontSize: 18 }}
                        >
                          {getFileIconFromExtension(unit.fileName)}
                        </span>
                      )}
                    </ThumbBox>
                    <div className="u-col u-fill" style={{ minWidth: 0 }}>
                      <div className="u-row u-gap8 u-items-center">
                        <span className="bodytext-13 u-text-ellipsis u-white-space">
                          {unit.label}
                        </span>
                        {unit.comparisonBase && (
                          <span
                            className="bodytext-11 u-text-blue"
                            style={{ display: "inline-flex", alignItems: "center", gap: 2, flexShrink: 0 }}
                          >
                            <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
                              link
                            </span>
                            オプション {comparisonLetters(unit)}
                          </span>
                        )}
                        {unit.takeCount > 1 && (
                          <span className="bodytext-11 u-text-light-gray" style={{ flexShrink: 0 }}>
                            全{unit.takeCount}テイク
                          </span>
                        )}
                      </div>
                      <span className="bodytext-11 u-text-light-gray u-text-ellipsis u-white-space">
                        {unit.fileName}
                      </span>
                    </div>
                  </div>
                </SelectableRow>
              );
            })}
          </div>
        </>
      )}
    </div>
  );

  const renderCommentRow = (
    comment: ShotComment,
    options: { isReply: boolean; selected: boolean; locked: boolean; onClick?: () => void },
  ) => {
    const { isReply, selected, locked, onClick } = options;
    const pinnedMedia = comment.media_item_id
      ? mediaItemsById.get(comment.media_item_id)
      : undefined;
    // 移動対象だがピン先メディアは移動しない → BE 側でピンが解除される
    const willLosePin =
      selected && !!comment.media_item_id && !selectedMediaIds.has(comment.media_item_id);
    return (
      <SelectableRow
        key={comment.id}
        $selected={selected}
        $disabled={locked || !onClick}
        onClick={onClick}
      >
        <div
          className="u-row u-gap8"
          style={{ alignItems: "flex-start", paddingLeft: isReply ? 32 : undefined }}
        >
          {isReply ? (
            <span
              className="material-symbols-rounded u-text-light-gray"
              style={{ fontSize: 18, flexShrink: 0, marginTop: 3 }}
            >
              subdirectory_arrow_right
            </span>
          ) : null}
          <SelectIcon $selected={selected} $locked={locked}>
            {selected ? "check_circle" : "radio_button_unchecked"}
          </SelectIcon>
          <UserAvatar
            userId={comment.author?.id}
            avatar={comment.author?.avatar}
            displayName={comment.author?.display_name}
            size={24}
          />
          <div className="u-col u-gap4 u-fill" style={{ minWidth: 0 }}>
            <div className="u-row u-gap8 u-items-center" style={{ flexWrap: "wrap" }}>
              <span className="bodytext-11">
                {comment.author?.display_name}
              </span>
              <span className="bodytext-11 u-text-light-gray">
                <DateDisplay date={comment.created_at} type={DATE_DISPLAY_TYPE.DATE_TIME} />
              </span>
              {comment.media_item_id && (
                <span
                  className="bodytext-11 u-text-light-gray"
                  style={{ display: "inline-flex", alignItems: "center", gap: 2 }}
                >
                  <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
                    push_pin
                  </span>
                  {pinnedMedia?.variation_name ?? "メディア"}
                  {comment.timecode_start ? ` ${comment.timecode_start}` : ""}
                </span>
              )}
              {comment.has_files && (
                <span
                  className="material-symbols-rounded u-text-light-gray"
                  style={{ fontSize: 14 }}
                >
                  attach_file
                </span>
              )}
            </div>
            <CommentExcerpt>
              {comment.comment ||
                (comment.files.length > 0 ? `ファイル ${comment.files.length} 件` : "")}
            </CommentExcerpt>
            {locked && !isReply && (
              <span className="bodytext-11 u-text-blue">
                選択したメディアと一緒に移動されます
              </span>
            )}
            {isReply && (
              <span className="bodytext-11 u-text-light-gray">
                親コメントと一体で移動されます
              </span>
            )}
            {willLosePin && (
              <span className="bodytext-11 u-text-light-gray">
                ※ ピン先のメディアは移動しないため、ピンは解除されます
              </span>
            )}
          </div>
        </div>
      </SelectableRow>
    );
  };

  const renderCommentStep = () => (
    <div className="u-col u-gap8">
      <div>
        <h2 className="heading-spacing-18">移動するコメントを選択</h2>
        <p className="bodytext-13 u-text-light-gray u-mt8">
          選択したメディアにピンされたコメントは自動で移動対象になります。
          返信は親コメントと一体で移動されます。
        </p>
        <p className="bodytext-11 u-text-light-gray u-mt8">選択中: {selectionSummary}</p>
      </div>

      {commentsError ? (
        <EmptyState>コメントの取得に失敗しました</EmptyState>
      ) : allComments === null ? (
        <div className="u-col-center u-w100 u-mt24">
          <SvgLoadingAnimation />
        </div>
      ) : commentNodes.length === 0 ? (
        <EmptyState>このショットにはコメントがありません</EmptyState>
      ) : (
        <div className="u-col u-w100">
          {commentNodes.map((node) => {
            const selected = effectiveCommentIds.has(node.comment.id);
            const locked = isParentLocked(node);
            return (
              <React.Fragment key={node.comment.id}>
                {renderCommentRow(node.comment, {
                  isReply: false,
                  selected,
                  locked,
                  onClick: locked ? undefined : () => toggleComment(node),
                })}
                {node.replies.map((reply) =>
                  renderCommentRow(reply, {
                    isReply: true,
                    selected: effectiveCommentIds.has(reply.id),
                    locked: true,
                  }),
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderTargetStep = () => (
    <div className="u-col u-gap8">
      <div>
        <h2 className="heading-spacing-18">移動先のショットを選択</h2>
        <p className="bodytext-13 u-text-light-gray u-mt8">
          「{sourceShot.shot_name}」から {selectionSummary} を移動します。
        </p>
      </div>

      {otherShots.length === 0 ? (
        <EmptyState>移動先にできるショットがありません</EmptyState>
      ) : (
        <>
          <Input
            name="shot-move-target-search"
            placeholder="ショット名で検索"
            value={targetSearch}
            onChange={(e) => setTargetSearch(e.target.value)}
          />
          {targetCandidates.length === 0 ? (
            <EmptyState>該当するショットがありません</EmptyState>
          ) : (
            <div className="u-col u-w100">
              {targetCandidates.map((shot) => {
                const selected = targetShotId === shot.id;
                const statusKey = getShotTableStatusKey(shot);
                return (
                  <SelectableRow
                    key={shot.id}
                    $selected={selected}
                    onClick={() => setTargetShotId(shot.id)}
                  >
                    <div className="u-row u-gap8 u-items-center">
                      <SelectIcon $selected={selected}>
                        {selected ? "radio_button_checked" : "radio_button_unchecked"}
                      </SelectIcon>
                      <span className="bodytext-13 u-fill u-text-ellipsis u-white-space" translate="no">
                        {shot.shot_name}
                      </span>
                      <span className="bodytext-11 u-text-light-gray" style={{ flexShrink: 0 }}>
                        {SHOT_TABLE_STATUS_LABELS[statusKey]}
                      </span>
                      <span
                        className="bodytext-11 u-text-light-gray"
                        style={{ flexShrink: 0, minWidth: 64, textAlign: "right" }}
                      >
                        メディア {shot.media_item_count} 件
                      </span>
                    </div>
                  </SelectableRow>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <Modal
      variant="fullscreen"
      onBack={onClose}
      headerExtras={
        <Stepper
          stepLabels={STEP_LABELS}
          current={step}
          onSelectStep={setStep}
          isStepClickable={(stepNumber) => stepNumber < step}
          showCheckOnComplete
        />
      }
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
    >
      <StepContent>
        {step === 1 && renderMediaStep()}
        {step === 2 && renderCommentStep()}
        {step === 3 && renderTargetStep()}
      </StepContent>
    </Modal>
  );
};

export default ShotMoveWizardModal;
